import React, { useState, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { axiosCaller } from '../../utils/apiCaller';
import { toast } from 'react-toastify';
import { convertNumberToHMS } from '../Admin/globalFunctions';

interface ReturnEvent {
    id: number;
    returnedAt: string;
    returnReason: string;
    categoryName: string | null;
    returnedBy: string | null;
    assignedTo: string | null;
    resolvedAt: string | null;
    resolvedBy: string | null;
    resolutionNotes: string | null;
    resolutionSeconds: number | null;
    isOpen: boolean;
}

interface Props {
    productId: number;
    responsibilityId: number;
    materialId?: number | null;
    title?: string;
    onClose: () => void;
    permissions?: any;
}

const ReturnHistoryDetail: React.FC<Props> = ({ productId, responsibilityId, materialId, title, onClose, permissions }) => {
    const intl = useIntl();
    const fm = (id: string, defaultMessage: string, values?: Record<string, any>) =>
        intl.formatMessage({ id, defaultMessage }, values);

    const [events, setEvents] = useState<ReturnEvent[]>([]);
    const [loading, setLoading] = useState(true);
    const [productName, setProductName] = useState('');
    const [resolutionInput, setResolutionInput] = useState<{ [key: number]: string }>({});
    const [savingId, setSavingId] = useState<number | null>(null);

    useEffect(() => {
        loadEvents();
    }, [productId, responsibilityId, materialId]);

    const loadEvents = () => {
        setLoading(true);
        const matParam = materialId ? `?materialId=${materialId}` : '';
        axiosCaller('GET', `rework-history/${productId}/${responsibilityId}${matParam}`).then((res: any) => {
            setEvents(res?.data?.events || []);
            setProductName(res?.data?.productName || '');
            setLoading(false);
        }).catch(() => {
            setLoading(false);
            toast.error(fm('production.returnHistory.toast.loadFailed', 'Nuk u ngarkuan të dhënat'));
        });
    };

    const saveResolutionNotes = (eventId: number) => {
        const notes = resolutionInput[eventId];
        if (!notes?.trim()) { toast.error(fm('production.returnHistory.toast.notesRequired', 'Shënimet janë obligative')); return; }
        setSavingId(eventId);
        axiosCaller('POST', `rework-history/${eventId}/resolution-notes`, { resolution_notes: notes }).then(() => {
            toast.success(fm('production.returnHistory.toast.notesSaved', 'Shënimet u ruajtën me sukses'));
            setSavingId(null);
            setResolutionInput(prev => { const n = { ...prev }; delete n[eventId]; return n; });
            loadEvents();
        }).catch(() => {
            toast.error(fm('production.toast.somethingWentWrong', 'Diçka nuk shkoi mirë!'));
            setSavingId(null);
        });
    };

    const formatResolution = (seconds: number | null): string => {
        if (!seconds) return '—';
        const d = Math.floor(seconds / 86400);
        const h = Math.floor((seconds % 86400) / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        const s = seconds % 60;
        if (d > 0) return `${d}d ${h}h ${m}m ${s}s`;
        if (h > 0) return `${h}h ${m}m ${s}s`;
        if (m > 0) return `${m}m ${s}s`;
        return `${s}s`;
    };

    return (
        <div className="agr-modal-overlay" onClick={onClose}>
            <div className="agr-modal" style={{ width: 520, maxWidth: '95vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
                <div className="agr-modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                    <div>
                        <div style={{ fontSize: 15, fontWeight: 600 }}><FormattedMessage id="production.returnHistory.title" defaultMessage="Historiku i kthimeve" /></div>
                        {(title || productName) && <div style={{ fontSize: 12, opacity: 0.8, marginTop: 2 }}>{title || productName}</div>}
                    </div>
                    <button onClick={onClose} type="button" style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 20 }}>&times;</button>
                </div>

                <div className="agr-modal-body" style={{ overflowY: 'auto', flex: 1, padding: '16px 20px' }}>
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: 32, color: '#94a3b8' }}><FormattedMessage id="production.board.loading" defaultMessage="Duke ngarkuar..." /></div>
                    ) : events.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: 32, color: '#94a3b8' }}><FormattedMessage id="common.noData" defaultMessage="Nuk ka të dhëna" /></div>
                    ) : (
                        <div style={{ position: 'relative', paddingLeft: 24 }}>
                            <div style={{ position: 'absolute', left: 7, top: 8, bottom: 8, width: 2, background: '#e2e8f0' }} />

                            {events.map((event, idx) => (
                                <div key={event.id} style={{ position: 'relative', marginBottom: idx < events.length - 1 ? 20 : 0 }}>
                                    <div style={{
                                        position: 'absolute', left: -20, top: 6, width: 12, height: 12, borderRadius: '50%',
                                        background: event.isOpen ? '#f59e0b' : '#22c55e', border: '2px solid #fff',
                                        boxShadow: '0 0 0 2px ' + (event.isOpen ? '#fef3c7' : '#dcfce7')
                                    }} />

                                    <div style={{
                                        background: event.isOpen ? '#fffbeb' : '#f8fafc',
                                        border: `1px solid ${event.isOpen ? '#fde68a' : '#e2e8f0'}`,
                                        borderRadius: 8, padding: '12px 14px'
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                            <span style={{
                                                fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5,
                                                padding: '2px 8px', borderRadius: 4,
                                                color: event.isOpen ? '#92400e' : '#166534',
                                                background: event.isOpen ? '#fef3c7' : '#dcfce7'
                                            }}>
                                                {event.isOpen
                                                    ? <FormattedMessage id="production.returnHistory.open" defaultMessage="E hapur" />
                                                    : <FormattedMessage id="production.returnHistory.resolved" defaultMessage="E zgjidhur" />}
                                            </span>
                                            <span style={{ fontSize: 11, color: '#94a3b8' }}>#{events.length - idx}</span>
                                        </div>

                                        <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.7 }}>
                                            <div>
                                                <FormattedMessage
                                                    id="production.returnHistory.returnedBy"
                                                    defaultMessage="Kthyer: {date} nga {by}"
                                                    values={{ date: event.returnedAt, by: <strong>{event.returnedBy || '—'}</strong> }}
                                                />
                                            </div>
                                            {event.assignedTo && (
                                                <div>
                                                    <FormattedMessage
                                                        id="production.returnHistory.assignedTo"
                                                        defaultMessage="Punëtori: {name}"
                                                        values={{ name: <strong>{event.assignedTo}</strong> }}
                                                    />
                                                </div>
                                            )}
                                            {event.categoryName && (
                                                <div style={{ marginTop: 4 }}>
                                                    <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, background: '#dbeafe', color: '#1e40af', fontWeight: 600 }}>
                                                        {event.categoryName}
                                                    </span>
                                                </div>
                                            )}
                                            <div style={{
                                                marginTop: 6, padding: '6px 10px', background: '#fff', border: '1px solid #e2e8f0',
                                                borderRadius: 6, fontStyle: 'italic', color: '#334155', lineHeight: 1.5
                                            }}>
                                                "{event.returnReason}"
                                            </div>
                                        </div>

                                        {!event.isOpen && (
                                            <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #e2e8f0', fontSize: 12, color: '#475569', lineHeight: 1.7 }}>
                                                <div>
                                                    <FormattedMessage
                                                        id="production.returnHistory.resolvedBy"
                                                        defaultMessage="Zgjidhur: {date} nga {by}"
                                                        values={{ date: event.resolvedAt, by: <strong>{event.resolvedBy || '—'}</strong> }}
                                                    />
                                                    {event.resolutionSeconds != null && (
                                                        <span style={{ marginLeft: 8, fontSize: 11, fontFamily: 'monospace', color: '#6b7280' }}>
                                                            ({formatResolution(event.resolutionSeconds)})
                                                        </span>
                                                    )}
                                                </div>
                                                {event.resolutionNotes ? (
                                                    <div style={{
                                                        marginTop: 6, padding: '6px 10px', background: '#f0fdf4', border: '1px solid #bbf7d0',
                                                        borderRadius: 6, color: '#166534', lineHeight: 1.5
                                                    }}>
                                                        <FormattedMessage
                                                            id="production.returnHistory.notesLabel"
                                                            defaultMessage="Shënime: {notes}"
                                                            values={{ notes: event.resolutionNotes }}
                                                        />
                                                    </div>
                                                ) : permissions?.fullPrivileges ? (
                                                    <div style={{ marginTop: 6 }}>
                                                        <textarea
                                                            value={resolutionInput[event.id] || ''}
                                                            onChange={e => setResolutionInput(prev => ({ ...prev, [event.id]: e.target.value }))}
                                                            placeholder={fm('production.returnHistory.notesPlaceholder', 'Shto shënime për zgjidhjen...')}
                                                            rows={2}
                                                            style={{ width: '100%', padding: '6px 8px', border: '1px solid #d0dbe8', borderRadius: 6, fontSize: 11, resize: 'none', outline: 'none', fontFamily: 'inherit' }}
                                                        />
                                                        <button
                                                            onClick={() => saveResolutionNotes(event.id)}
                                                            disabled={savingId === event.id}
                                                            type="button"
                                                            style={{
                                                                marginTop: 4, padding: '4px 12px', fontSize: 11, fontWeight: 600,
                                                                background: 'linear-gradient(135deg, #1e3a5f, #2563eb)', color: '#fff',
                                                                border: 'none', borderRadius: 4, cursor: 'pointer', opacity: savingId === event.id ? 0.6 : 1
                                                            }}
                                                        >
                                                            {savingId === event.id
                                                                ? <FormattedMessage id="production.returnHistory.saving" defaultMessage="Duke ruajtur..." />
                                                                : <FormattedMessage id="production.returnHistory.saveNotes" defaultMessage="Ruaj shënimet" />}
                                                        </button>
                                                    </div>
                                                ) : null}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ReturnHistoryDetail;
